import {
  AlertDialog,
  Checkbox,
  DatePicker,
  Icon,
  InputDropdown,
  NewTable,
  SearchBar,
  Section,
  Text,
  useToast,
} from '@vallourec-ui/vallourec-ui'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { isAxiosError } from 'axios'

import { ca } from 'date-fns/locale'

import {
  generateReport as generateReportPost,
  getCollapseCurve,
} from '@/api/post'

import { useGenerateReport } from '@/stores/GenerateReportStore'

import DifferentHeaderModal from '../DifferentHeaderModal'
import {
  ButtonContainer,
  Row,
  SearchBarContainer,
  StyledButton,
  StyledInputLabel,
  StyledTextButton,
  StyledText,
  ContentContainer,
} from './style'
import {
  CollapseCurve,
  DataResultProps,
  DataResultTemplate,
} from '@/@types/global'
import { ButtonsContainer } from '@/components/ImageCard/style'
import TextFieldInput from '@/components/TextFieldInput'
import DataGrid from '@/components/dataGrid'
import OverallReportChart from '@/components/overallReportChart/overAllReportChart'
import { TableWrapper } from '@/pages/Flow/_component/DataToResult/style'
import { useApp } from '@/stores/AppStore'
import { SITES } from '@/utils/constants'
import { generatePdfFile } from '@/utils/pdfGenerator/pdfGenerator'
import { createRowResults, getDateNow, getDateTimeNow } from '@/utils/utils'

const DataToResult: React.FC = () => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const {
    setIsLoading,
    templateColumn,
    templateRow,
    setTemplateRow,
    resultItems,
    setExecuteSearch,
    user,
  } = useApp()
  const {
    site,
    orderItem,
    type,
    clientHeader,
    descriptionHeader,
    inspectionHeader,
    projectHeader,
    pedidoItemHeader,
    remark1,
    remark2,
    remark3,
    setClientHeader,
    setDescriptionHeader,
    setProjectHeader,
    setInspectionHeader,
    setPedidoItemHeader,
    setCurrentItem,
    setResultItemsSelected,
    resultItemsSelected,
    testSelected,
    currentItem,
    heatItems,
    completeHeaderItem,
    base64Graphics,
    setBase64Graphics,
    generateNewReport,
  } = useGenerateReport()

  const [initialized, setInitialized] = useState(false)
  const [checkedAll, setCheckedAll] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [descriptionDocuments, setDescriptionDocuments] = useState('')
  const [collapseCurve, setCollapseCurve] = useState<CollapseCurve[]>([])
  const [setItemsPerPage] = useState<number>(50)

  const [differentHeaderModalOpen, setDifferentHeaderModalOpen] =
    useState(false)

  useEffect(() => {
    if (templateRow.length > 0 && (!initialized || !templateRow[0].cell)) {
      addCellRow(templateRow)
      setTemplateRow([...templateRow])
      setInitialized(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, templateRow, setTemplateRow])

  const headerEditSectionRef = useRef({
    completeItem: completeHeaderItem,
    customer: clientHeader,
    description: descriptionHeader,
    inspection: inspectionHeader,
    project: projectHeader,
    orderItemHeader: pedidoItemHeader,
    remark1Header: remark1,
    remark2Header: remark2,
    remark3Header: remark3,
  })

  useEffect(() => {
    headerEditSectionRef.current = {
      completeItem: completeHeaderItem,
      customer: clientHeader,
      description: descriptionHeader,
      inspection: inspectionHeader,
      project: projectHeader,
      orderItemHeader: pedidoItemHeader,
      remark1Header: remark1,
      remark2Header: remark2,
      remark3Header: remark3,
    }
  }, [
    completeHeaderItem,
    clientHeader,
    descriptionHeader,
    inspectionHeader,
    projectHeader,
    pedidoItemHeader,
    remark1,
    remark2,
    remark3,
  ])

  const validatePermissionReport = (
    optionGenerate: string,
    typeCode: string
  ) => {
    let hasPermission = false
    if (user && user.permissions) {
      switch (optionGenerate) {
        case 'generateReport':
          hasPermission =
            user.permissions[typeCode.toLowerCase()].includes('elaborate') ||
            user.permissions[typeCode.toLowerCase()].includes('view')
          break
        case 'sendApprovalReport':
          hasPermission =
            user.permissions[typeCode.toLowerCase()].includes('elaborate')
          break
        default:
          break
      }
    }
    return hasPermission
  }

  const validateItemsSelectedReportNaceD = () => {
    if (
      testSelected?.typeCode === 'corrosion' &&
      testSelected.id === 'nace_d' &&
      resultItemsSelected
    ) {
      let id_ensaio = ''
      for (const item of resultItemsSelected) {
        if (item.id_ensaio) {
          if (id_ensaio == '') {
            id_ensaio = item.id_ensaio
          } else if (item.id_ensaio !== id_ensaio) {
            return true
          }
        }
      }
    }
    return false
  }

  const validateOneTestReport = () => {
    const allowOnlyOneTests = [
      'ctdo',
      'reeling',
      'collapse',
      'micro_cleanliness_d',
    ]
    const allowOnlyOneTestsLot = ['mpi']

    if (
      testSelected &&
      resultItemsSelected &&
      resultItemsSelected.length > 1 &&
      (allowOnlyOneTests.includes(testSelected.id) ||
        allowOnlyOneTestsLot.includes(testSelected.id))
    ) {
      if (allowOnlyOneTestsLot.includes(testSelected.id)) {
        const count: any[] = []
        for (const item of resultItemsSelected) {
          if (!count.includes(item.lot_id)) {
            count.push(item.lot_id)
          }
        }
        if (count.length > 1) {
          setResultItemsSelected([])
          return true
        }
      } else {
        setResultItemsSelected([])
        return true
      }
    }

    return false
  }

  const canGenerate = async (optionGenerate = '', justGenerate = false) => {
    let valid = true
    const permissionReport = testSelected
      ? validatePermissionReport(optionGenerate, testSelected.typeCode)
      : false
    if (!permissionReport) {
      toast({
        title: 'Error',
        description: t('user.permission_not_action'),
        type: 'error',
      })
      valid = false
    } else {
      if (!justGenerate && (!user || !user.hasSignature)) {
        toast({
          title: 'Error',
          description: t('user.signature_not_exists'),
          type: 'error',
        })
        valid = false
      }
      if (!resultItemsSelected || !resultItemsSelected.length) {
        toast({
          title: 'Error',
          description: t('generateReport.dataToResult.select_test'),
          type: 'error',
        })
        valid = false
      }
      if (validateOneTestReport()) {
        toast({
          title: 'Error',
          description: t('generateReport.dataToResult.one_test'),
          type: 'error',
        })
        valid = false
      }
      if (validateItemsSelectedReportNaceD()) {
        toast({
          title: 'Error',
          description: t('generateReport.dataToResult.multiples_test'),
          type: 'error',
        })
        valid = false
      }
    }

    return valid
  }

  const isDifferentHeader = () => {
    const differenceItemsHeader = getDifferentHeader()
    return differenceItemsHeader.length > 1
  }

  const findItem = (test: any, differenceItemsHeader: any) => {
    const exist = differenceItemsHeader.find((edit: any) => {
      return (
        edit.desc_cliente === test.desc_cliente &&
        edit.descricao_mes === test.descricao_mes &&
        edit.project === test.project &&
        edit.inspection === test.inspection &&
        edit.pedido === test.pedido &&
        edit.item === test.item
      )
    })
    return !exist
  }

  const generateReport = async (
    justGenerateValue = false,
    generateExcel = false
  ) => {
    const elaborationPosition = user?.userPosition ?? ' '
    const elaborationName = user?.userName ?? ' '

    const {
      completeItem,
      customer,
      description,
      inspection,
      project,
      orderItemHeader,
      remark1Header,
      remark2Header,
      remark3Header,
    } = headerEditSectionRef.current

    if (
      testSelected &&
      currentItem &&
      completeItem &&
      resultItemsSelected &&
      user &&
      user.userPosition
    ) {
      let success = false
      try {
        await generateReportPost({
          email: user.email,
          user: user.userName,
          position: user?.userPosition,
          test: testSelected,
          generate: true,
          dateNow: getDateNow(),
          testName: testSelected?.name,
          selected: resultItemsSelected,
          remark1: remark1Header,
          remark2: remark2Header,
          remark3: remark3Header,
          site: SITES.find((s) => s.name === site),
          heat: heatItems,
          documents: [],
          currentDate: getDateTimeNow().en,
          // requirements: this.requirementsValue,
          elaboration: elaborationName,
          elaboration_position: elaborationPosition,
          ...(base64Graphics ? { base64Graphics } : {}),
          customFields: {
            completeHeaderItem: completeItem,
            customer,
            description,
            inspection,
            project,
            orderItem: orderItemHeader,
            justGenerate: justGenerateValue,
            generateExcel,
          },
        })

        success = true
        toast({
          title: 'Success',
          description: t('application.commons.success.operation'),
          type: 'success',
        })
      } catch (error) {
        console.log(error)
        if (isAxiosError(error)) {
          toast({
            title: 'Error',
            description: error.response?.data?.errorMessage ?? error.message,
            type: 'error',
          })
        } else {
          toast({
            title: 'Error',
            description: `${error}`,
            type: 'error',
          })
        }
      } finally {
        if (success) {
          setExecuteSearch(true)
        }
      }
    }
  }

  const generate = async (
    justGenerate = false,
    optionGenerate = 'generateReport'
  ) => {
    let search = true
    try {
      setIsLoading(true)
      if (user && user.userPosition) {
        const isCanGenerate = await canGenerate(optionGenerate, justGenerate)
        if (isCanGenerate && resultItemsSelected) {
          const documents = []
          const versions: string[] = []

          for (const itemDoc of resultItemsSelected) {
            if (itemDoc.id_document) {
              documents.push(itemDoc.id_document)
              if (!versions.includes(itemDoc.document_version))
                versions.push(itemDoc.document_version)
            }
          }

          if (documents && documents.length) {
            setShowConfirm(true)
            setDescriptionDocuments(
              versions?.map((item) => `- ${item}.`).join('\n')
            )
          } else if (
            testSelected?.typeCode === 'corrosion' &&
            isDifferentHeader()
          ) {
            setDifferentHeaderModalOpen(true)
          } else if (
            testSelected?.typeCode === 'corrosion' &&
            isDifferentHeader()
          ) {
            setDifferentHeaderModalOpen(true)
          } else if (testSelected?.id === 'microstructure_photos' || testSelected?.id === 'microstructure_photos_longitudinal') {
            const {
              completeItem,
              customer,
              description,
              inspection,
              project,
              orderItemHeader,
              remark1Header,
              remark2Header,
              remark3Header,
            } = headerEditSectionRef.current

            await generateNewReport({
              justGenerateValue: justGenerate,
              generateExcel: false,
              user,
              site,
              remark1: remark1Header,
              remark2: remark2Header,
              remark3: remark3Header,
              customFields: {
                completeHeaderItem: completeItem,
                customer,
                description,
                inspection,
                project,
                orderItem: orderItemHeader,
              },
            })
          } else {
            await generateReport(justGenerate, false)
          }
        }
      } else {
        toast({
          title: 'Error',
          description: t('user.not_position'),
          type: 'error',
        })
      }
    } catch (error) {
      search = false
    } finally {
      setIsLoading(false)
    }
  }

  const getColumns = (columns: any) => {
    return [
      {
        id: 'cell',
        accessorKey: 'cell',
        header: () => (
          <Checkbox
            checked={checkedAll}
            label=""
            onCheckedChange={() => fillItems()}
          />
        ),
        cell: ({ cell }) => cell.getValue(),
      },
      {
        id: 'cell2',
        accessorKey: 'cell2',
        header: '',
        cell: ({ cell }) => cell.getValue(),
      },
      ...columns,
    ]
  }

  const fillHeader = (index: number, value: boolean) => {
    const item = resultItems[index]
    setClientHeader(`${value && item.desc_cliente ? item.desc_cliente : ''}`)
    setDescriptionHeader(value ? item.descricao_mes : '')
    setProjectHeader(`${value && item.project ? item.project : ''}`)
    setInspectionHeader(value ? item.firma_insp : '')
    setPedidoItemHeader(
      value
        ? `${item.pedido ? item.pedido : ''}/${item.item ? item.item : ''}`
        : ''
    )

    const newTemplateRow = templateRow.map((element, idx) => {
      element.checkedHeader = idx === index ? !element.checkedHeader : false
      return element
    })
    setBase64Graphics(undefined)
    setCollapseCurve([])
    addCellRow(newTemplateRow)
    setTemplateRow(newTemplateRow)
    setCurrentItem(value ? item : undefined)
    fillResultChecked(resultItems, newTemplateRow)
  }

  useEffect(() => {
    const fetchData = async () => {
      if (currentItem && testSelected?.typeCode === 'collapse') {
        try {
          setIsLoading(true)
          const response = await getCollapseCurve({
            sample: currentItem.id_numeric,
          })
          setCollapseCurve(response.items)
        } catch (error) {
          console.log(error)
          if (isAxiosError(error)) {
            toast({ title: 'Error', description: error.message, type: 'error' })
          }
        } finally {
          setIsLoading(false)
        }
      } else if (collapseCurve.length) {
        setCollapseCurve([])
      }

      if (!currentItem) {
        setClientHeader('')
        setDescriptionHeader('')
        setProjectHeader('')
        setInspectionHeader('')
        setPedidoItemHeader('')
        setBase64Graphics(undefined)
        setCollapseCurve([])
      }
    }

    fetchData()
  }, [currentItem])

  const fillResultChecked = (
    resultItems: any[],
    templateRow: DataResultTemplate[]
  ) => {
    const itemsSelected = templateRow
      .filter((item) => item.checked)
      ?.map((item) => item.index)
    setResultItemsSelected(
      resultItems.filter((item, index) => {
        item.indexResultItems = index
        return itemsSelected.includes(index)
      })
    )
  }

  const fillItems = (index?: number) => {
    if (index === undefined) setCheckedAll(!checkedAll)
    const newTemplateRow = templateRow.map((element, idx) => {
      if (index === undefined) {
        element.checked = !checkedAll
      } else if (idx === index) {
        element.checked = !element.checked
      }
      return element
    })
    addCellRow(newTemplateRow)
    setTemplateRow(newTemplateRow)
    fillResultChecked(resultItems, newTemplateRow)
  }

  const addCellRow = (newTemplateRow: any) => {
    newTemplateRow.forEach((element: any, index: number) => {
      ;(element.cell = (
        <Checkbox
          id={`checked-${index}`}
          name={`checked-${index}`}
          checked={!!element.checked}
          label=""
          onCheckedChange={() => fillItems(index)}
        />
      )),
        (element.cell2 = (
          <Checkbox
            id={`checked2-${index}`}
            name={`checked2-${index}`}
            checked={element.checkedHeader}
            label=""
            onCheckedChange={(value) => fillHeader(index, value)}
          />
        ))
    })
  }

  const renderTable = useCallback(() => {
    return templateColumn.length > 0 && templateRow.length > 0 ? (
      <TableWrapper>
        <NewTable.Root
          key="Table-with-result"
          columnIdWithRowspan="button"
          columns={getColumns(templateColumn)}
          data={templateRow}
          isBordered
          hasScroll
          simplePagination={
            <NewTable.Pagination
              key="NewTable-Pagination-results"
              previousPage={() => 0}
              nextPage={() => 0}
              getPageCount={() => 1}
              getCanPreviousPage={() => false}
              getCanNextPage={() => false}
              getState={() => {
                return {
                  pagination: {
                    pageIndex: 0,
                    pageSize: 1,
                  },
                }
              }}
              setPageSize={setItemsPerPage}
            />
          }
        />
        <Row
          style={{
            justifyContent: 'flex-end',
            marginTop: '15px',
            marginBottom: '15px',
          }}
        >
          <ButtonContainer>
            <StyledButton onClick={() => generate(false, 'sendApprovalReport')}>
              <StyledTextButton>
                <Icon name="CheckLine" color="white" />
                {t<string>(
                  'generateReport.dataToEditSection.sendToApproval',
                  'Send to Approval Flow'
                )}
              </StyledTextButton>
            </StyledButton>
          </ButtonContainer>
        </Row>
      </TableWrapper>
    ) : (
      <NewTable.Root key="Table-with-result-no-data" data={[]} columns={[]} />
    )
  }, [templateColumn, templateRow, t])

  const confirm = async () => {
    try {
      setIsLoading(true)
      setShowConfirm(false)
      if (testSelected?.id === 'microstructure_photos' || testSelected?.id === 'microstructure_photos_longitudinal') {
        const {
          completeItem,
          customer,
          description,
          inspection,
          project,
          orderItemHeader,
          remark1Header,
          remark2Header,
          remark3Header,
        } = headerEditSectionRef.current

        await generateNewReport({
          justGenerateValue: false,
          generateExcel: false,
          user,
          site,
          remark1: remark1Header,
          remark2: remark2Header,
          remark3: remark3Header,
          customFields: {
            completeHeaderItem: completeItem,
            customer,
            description,
            inspection,
            project,
            orderItem: orderItemHeader,
          },
        })
      } else {
        await generateReport(false, false)
      }
    } catch (error) {
      console.log(error)
      if (isAxiosError(error)) {
        toast({ title: 'Error', description: error.message, type: 'error' })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onOutsideClickAlert = () => {
    setShowConfirm(false)
  }

  const getDifferentHeader = () => {
    const differenceItemsHeader = []
    let edit = null

    if (resultItemsSelected) {
      for (const item of resultItemsSelected) {
        const test = {
          desc_cliente: item.desc_cliente,
          descricao_mes: item.descricao_mes,
          project: item.project,
          inspection: item.firma_insp,
          pedido: item.pedido,
          item: item.item,
        }
        if (!edit) {
          edit = test
          differenceItemsHeader.push(edit)
        }
        if (
          (edit.customer !== test.desc_cliente ||
            edit.description !== test.description ||
            edit.project !== test.project ||
            edit.inspection !== test.inspection ||
            (edit.pedido !== test.pedido && edit.item !== test.item)) &&
          findItem(test, differenceItemsHeader)
        ) {
          differenceItemsHeader.push(test)
        }
      }
    }
    return differenceItemsHeader
  }

  const generateDifferentHeader = async () => {
    try {
      setIsLoading(true)
      if (testSelected?.id === 'microstructure_photos' || testSelected?.id === 'microstructure_photos_longitudinal') {
        const {
          completeItem,
          customer,
          description,
          inspection,
          project,
          orderItemHeader,
          remark1Header,
          remark2Header,
          remark3Header,
        } = headerEditSectionRef.current

        await generateNewReport({
          justGenerateValue: false,
          generateExcel: false,
          user,
          site,
          remark1: remark1Header,
          remark2: remark2Header,
          remark3: remark3Header,
          customFields: {
            completeHeaderItem: completeItem,
            customer,
            description,
            inspection,
            project,
            orderItem: orderItemHeader,
          },
        })
      } else {
        await generateReport(false, false)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {testSelected?.typeCode === 'collapse' && (
        <OverallReportChart data={collapseCurve} />
      )}
      <ContentContainer>{renderTable()}</ContentContainer>
      <DifferentHeaderModal
        isOpen={differentHeaderModalOpen}
        setIsOpen={setDifferentHeaderModalOpen}
        onOutsideClick={() => setDifferentHeaderModalOpen(false)}
        onConfirm={() => generateDifferentHeader()}
        textButtonConfirm={t<string>(
          'generateReport.dataToEditSection.sendToApproval',
          'Send to Approval Flow'
        )}
        items={getDifferentHeader()}
      />
      <AlertDialog
        open={showConfirm}
        variant="warn"
        title={`${t<string>('generateReport.dataToResult.hasDocuments')}`}
        description={descriptionDocuments}
        onOpenChange={onOutsideClickAlert}
        close={onOutsideClickAlert}
        footer={
          <ButtonsContainer>
            <StyledButton
              onClick={onOutsideClickAlert}
              key="cancel-button"
              variant="warnWithBorders"
            >
              <Text>{t<string>('application.button.cancel')}</Text>
            </StyledButton>
            <StyledButton
              onClick={confirm}
              key="confirm-button"
              variant="warnWithoutBorders"
            >
              <Text>{t<string>('application.button.confirm')}</Text>
            </StyledButton>
          </ButtonsContainer>
        }
      />
    </>
  )
}

export default DataToResult
